/**
 * Experience Engine — client helpers.
 * Switch only on presentation.type; durable anonymous state in localStorage.
 */

import configuration from "react-global-configuration";
import api from "../../Environment";
import {
  getPublicPageBySlug,
  isKidsSectionEnabled,
  isShopSectionEnabled,
} from "../Helper/siteContent";
import {
  isPublicBrowseExperienceSlug,
  routeHasDestinationExperienceGate as routeHasDestinationExperienceGateCore,
} from "./experienceRouteCoverage";

/** Operational floors — mirrors backend ExperiencePresentationGateway::TIMING_MIN. */
export const TIMING_MIN = {
  fade_in_ms: 100,
  fade_out_ms: 100,
  skip_after_ms: 0,
  duration_ms: 800,
};

export const TIMING_DEFAULTS = {
  fade_in_ms: 400,
  fade_out_ms: 400,
  skip_after_ms: 1500,
  duration_ms: 2500,
};

/**
 * Clamp invalid / legacy timing (e.g. fade=3, duration=3) to operable floors.
 * Intentionally short-but-visible configs at/above mins are preserved.
 */
export function normalizeTiming(timing) {
  const src = timing && typeof timing === "object" ? timing : {};
  const out = {};
  Object.keys(TIMING_DEFAULTS).forEach(function (key) {
    var raw = src[key];
    var value = parseInt(raw, 10);
    if (isNaN(value) || value < 0) {
      value = TIMING_DEFAULTS[key];
    }
    var min = TIMING_MIN[key] != null ? TIMING_MIN[key] : 0;
    if (value < min) {
      value = min > 0 ? min : TIMING_DEFAULTS[key];
    }
    out[key] = value;
  });
  return out;
}

export function nonePresentation() {
  return {
    type: "none",
    should_present: false,
    component: null,
    media: {},
    timing: {},
    behavior: {
      continue_immediately: true,
      display_frequency: "once_per_session",
      allow_permanent_dismissal: false,
    },
    analytics: {},
    meta: {},
  };
}

export function normalizePresentation(raw) {
  if (!raw || typeof raw !== "object") {
    return nonePresentation();
  }
  const type = raw.type || "none";
  const should =
    !!raw.should_present && type !== "none" && hasPresentableMedia(raw);
  const timing = normalizeTiming(raw.timing || {});
  return {
    ...nonePresentation(),
    ...raw,
    type: should ? type : "none",
    should_present: should,
    timing: should ? timing : {},
    behavior: {
      ...nonePresentation().behavior,
      ...(raw.behavior || {}),
    },
  };
}

export function hasPresentableMedia(presentation) {
  if (!presentation || !presentation.should_present) {
    return false;
  }
  const type = presentation.type;
  const media = presentation.media || {};
  if (type === "splash") {
    return !!(media.image || media.background_color);
  }
  if (type === "cinematic") {
    return !!media.video;
  }
  return false;
}

export function getPlatformExperienceFromSettings() {
  try {
    return normalizePresentation(
      configuration.get("configData.experience")
    );
  } catch (e) {
    return nonePresentation();
  }
}

function experienceId(presentation) {
  return (
    (presentation &&
      presentation.meta &&
      presentation.meta.experience_id) ||
    (presentation &&
      presentation.analytics &&
      presentation.analytics.configuration_id) ||
    (presentation &&
      presentation.meta &&
      presentation.meta.configuration_id) ||
    null
  );
}

export function destinationScopeId(presentation) {
  const dest =
    presentation &&
    presentation.meta &&
    presentation.meta.destination;
  if (dest && dest.destination_id != null && dest.destination_id !== "") {
    return String(dest.destination_id);
  }
  return "";
}

function resolveDestinationScope(destinationType, presentation) {
  if (destinationType) {
    return destinationType;
  }
  const dest =
    presentation &&
    presentation.meta &&
    presentation.meta.destination;
  return (dest && dest.destination_type) || "platform";
}

function storageKey(destinationType, presentation) {
  const id = experienceId(presentation);
  const scope = resolveDestinationScope(destinationType, presentation);
  const scopeId = destinationScopeId(presentation);
  return `ee.state.${scope}.${scopeId}.${id || "unknown"}`;
}

function sessionKey(destinationType, presentation) {
  const id = experienceId(presentation);
  const scope = resolveDestinationScope(destinationType, presentation);
  const scopeId = destinationScopeId(presentation);
  return `ee.session.${scope}.${scopeId}.${id || "unknown"}`;
}

export function presentationScopeKey(destinationType, presentation) {
  const normalized = normalizePresentation(presentation);
  const scope = resolveDestinationScope(destinationType, normalized);
  const scopeId = destinationScopeId(normalized);
  const id = experienceId(normalized) || "none";
  return `${scope}:${scopeId}:${id}:${normalized.type}`;
}

const publicBrowseRouteDeps = {
  getPublicPageBySlug,
  isKidsSectionEnabled,
  isShopSectionEnabled,
};

/**
 * Public browse catch-all (/:publicSlug) when PublicBrowseGate renders Genres.
 */
export function isPublicBrowseSlugWithExperienceGate(pathname) {
  return isPublicBrowseExperienceSlug(pathname, publicBrowseRouteDeps);
}

/**
 * Routes that mount their own destination ExperienceGate (independent of Platform).
 * Platform entry splash must not stack over or override these destinations.
 */
export function routeHasDestinationExperienceGate(pathname) {
  return routeHasDestinationExperienceGateCore(pathname, publicBrowseRouteDeps);
}

export function readAnonymousState(presentation, destinationType) {
  const id = experienceId(presentation);
  if (!id) {
    return null;
  }
  try {
    const raw = localStorage.getItem(storageKey(destinationType, presentation));
    const parsed = raw ? JSON.parse(raw) : {};
    let sessionSeen = false;
    try {
      sessionSeen =
        sessionStorage.getItem(sessionKey(destinationType, presentation)) ===
        "1";
    } catch (e) {}
    return {
      experience_id: id,
      last_seen_at: parsed.last_seen_at || null,
      do_not_show_again: !!parsed.do_not_show_again,
      total_views: parsed.total_views || 0,
      session_seen: sessionSeen,
      scope: parsed.scope || null,
      scope_id: parsed.scope_id != null ? parsed.scope_id : null,
    };
  } catch (e) {
    return null;
  }
}

export function writeAnonymousState(presentation, destinationType, patch) {
  const id = experienceId(presentation);
  if (!id) {
    return;
  }
  try {
    const current =
      readAnonymousState(presentation, destinationType) || {
      experience_id: id,
      total_views: 0,
    };
    const next = {
      ...current,
      ...patch,
      experience_id: id,
      scope: destinationType || current.scope || "platform",
      scope_id:
        presentation &&
        presentation.meta &&
        presentation.meta.destination &&
        presentation.meta.destination.destination_id != null
          ? presentation.meta.destination.destination_id
          : current.scope_id,
    };
    localStorage.setItem(storageKey(destinationType, presentation), JSON.stringify(next));
  } catch (e) {
    // ignore
  }
}

/**
 * Client-side frequency gate for anonymous (and session) viewing.
 */
export function shouldShowClientSide(presentation, destinationType) {
  const normalized = normalizePresentation(presentation);
  if (!normalized.should_present) {
    return false;
  }

  const frequency =
    (normalized.behavior && normalized.behavior.display_frequency) ||
    "once_per_session";
  const state = readAnonymousState(normalized, destinationType);

  if (state && state.do_not_show_again) {
    return false;
  }

  if (frequency === "every_visit") {
    return true;
  }

  if (frequency === "once_per_session") {
    return !(state && state.session_seen);
  }

  if (frequency === "once_per_day") {
    if (!state || !state.last_seen_at) {
      return true;
    }
    try {
      const last = new Date(state.last_seen_at);
      const now = new Date();
      return (
        last.getFullYear() !== now.getFullYear() ||
        last.getMonth() !== now.getMonth() ||
        last.getDate() !== now.getDate()
      );
    } catch (e) {
      return true;
    }
  }

  if (frequency === "once_ever") {
    return !(state && (state.last_seen_at || state.total_views > 0));
  }

  return true;
}

export function markExperienceSeen(destinationType, presentation) {
  const normalized = normalizePresentation(presentation);
  const id = experienceId(normalized);
  const now = new Date().toISOString();
  const prev =
    readAnonymousState(normalized, destinationType) || { total_views: 0 };

  writeAnonymousState(normalized, destinationType, {
    last_seen_at: now,
    total_views: (prev.total_views || 0) + 1,
  });

  try {
    if (id) {
      sessionStorage.setItem(
        sessionKey(destinationType, normalized),
        "1"
      );
    }
  } catch (e) {}

  const userId = localStorage.getItem("userId");
  const token = localStorage.getItem("accessToken");
  if (userId && token && id) {
    try {
      api.postMethod("experience/state/record", {
        id: userId,
        experience_id: id,
        scope: destinationType || "platform",
        scope_id:
          normalized.meta &&
          normalized.meta.destination &&
          normalized.meta.destination.destination_id != null
            ? normalized.meta.destination.destination_id
            : "",
      });
    } catch (e) {}
  }
}

export function dismissExperiencePermanently(destinationType, presentation) {
  const normalized = normalizePresentation(presentation);
  writeAnonymousState(normalized, destinationType, {
    do_not_show_again: true,
    last_seen_at: new Date().toISOString(),
  });

  const id = experienceId(normalized);
  const userId = localStorage.getItem("userId");
  const token = localStorage.getItem("accessToken");
  if (userId && token && id) {
    try {
      api.postMethod("experience/state/dismiss", {
        id: userId,
        experience_id: id,
        scope: destinationType || "platform",
        scope_id:
          normalized.meta &&
          normalized.meta.destination &&
          normalized.meta.destination.destination_id != null
            ? normalized.meta.destination.destination_id
            : "",
      });
    } catch (e) {}
  }
}

/**
 * Sync anonymous localStorage states after login.
 */
export async function syncAnonymousExperienceStateAfterLogin() {
  const userId = localStorage.getItem("userId");
  const token = localStorage.getItem("accessToken");
  if (!userId || !token) {
    return;
  }

  const states = [];
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key || key.indexOf("ee.state.") !== 0) {
        continue;
      }
      const raw = localStorage.getItem(key);
      if (!raw) {
        continue;
      }
      const parsed = JSON.parse(raw);
      if (parsed && parsed.experience_id) {
        states.push(parsed);
      }
    }
  } catch (e) {
    return;
  }

  if (!states.length) {
    return;
  }

  try {
    await api.postMethod("experience/state/sync", {
      id: userId,
      states: JSON.stringify(states),
    });
  } catch (e) {
    // never block
  }
}

export function emitExperienceHook(presentation, hookName, detail = {}) {
  try {
    const hooks =
      presentation && presentation.analytics && presentation.analytics.hooks
        ? presentation.analytics.hooks
        : {};
    const eventName = hooks[hookName] || `experience.${hookName}`;
    if (typeof window !== "undefined" && window.dispatchEvent) {
      window.dispatchEvent(
        new CustomEvent(eventName, {
          detail: { presentation, ...detail },
        })
      );
    }
  } catch (e) {
    // never block
  }

  // Fire-and-forget lifecycle analytics (does not change presentation contracts).
  try {
    trackExperienceAnalytics(hookName, presentation, detail);
  } catch (e) {}
}

/**
 * POST experience/analytics/track — optional client lifecycle events.
 */
export function trackExperienceAnalytics(eventName, presentation, detail = {}) {
  const map = {
    impression: "impression",
    started: "cinematic_started",
    cinematic_started: "cinematic_started",
    completed: "cinematic_completed",
    cinematic_completed: "cinematic_completed",
    splash_displayed: "splash_displayed",
    action_clicked: "action_clicked",
    dismissed: "dismissed",
    skipped: "skipped",
    timed_out: "timed_out",
    auto_closed: "auto_closed",
  };
  const event = map[eventName] || eventName;
  const allowed = [
    "impression",
    "cinematic_started",
    "cinematic_completed",
    "splash_displayed",
    "action_clicked",
    "dismissed",
    "skipped",
    "timed_out",
    "auto_closed",
  ];
  if (allowed.indexOf(event) === -1) {
    return;
  }

  const id = experienceId(presentation);
  if (!id) {
    return;
  }

  const payload = {
    event,
    experience_id: id,
    id: localStorage.getItem("userId") || "",
    destination_type:
      (detail && detail.destinationType) ||
      (presentation &&
        presentation.meta &&
        presentation.meta.destination &&
        presentation.meta.destination.destination_type) ||
      "platform",
    destination_id:
      (detail && detail.destinationId) ||
      (presentation &&
        presentation.meta &&
        presentation.meta.destination &&
        presentation.meta.destination.destination_id) ||
      "",
    watch_percentage:
      detail && detail.watch_percentage != null ? detail.watch_percentage : "",
    provider:
      presentation && presentation.meta && presentation.meta.liturgical_provider
        ? presentation.meta.liturgical_provider
        : "",
    season:
      presentation && presentation.meta && presentation.meta.season_key
        ? presentation.meta.season_key
        : "",
    experiment_variant:
      presentation && presentation.meta && presentation.meta.experiment_variant
        ? presentation.meta.experiment_variant
        : "",
  };

  try {
    api.postMethod("experience/analytics/track", payload);
  } catch (e) {
    // never block
  }
}

// Back-compat aliases used by older gate code paths.
export function experienceSessionKey(destinationType, presentation) {
  return sessionKey(destinationType, presentation);
}

export function hasSeenExperience(destinationType, presentation) {
  return !shouldShowClientSide(presentation, destinationType);
}
